import { useCallback, useEffect, useMemo, useState } from 'react'
import Footer from '@/components/Footer'
import LocationForm from '@/components/LocationForm'
import MapView from '@/components/Map'
import ResultCard from '@/components/ResultCard'
import { useLocationUrlSync } from '@/hooks/useLocationUrlSync'
import { useNearbyStations } from '@/hooks/useNearbyStations'
import { centroid, geometricMedian, selectKMedoidStation } from '@/lib/geo'
import { buildShareUrl, getInitialLocationsFromUrl } from '@/lib/urlState'
import type { LatLng, Location, MapFocusRequest, MeetingPointResult } from '@/types'

/** Maximum number of locations allowed */
const MAX_LOCATIONS = 10

/**
 * Raw nearby station rows fetched per reference point.
 * Large enough to (a) serve as the K-medoid candidate pool for the Median,
 * and (b) ensure at least 3 unique station names survive group-by-name even
 * when the reference point coincides with a multi-line station.
 */
const NEARBY_STATION_LIMIT = 20

function App() {
  const [locations, setLocations] = useState<Location[]>(getInitialLocationsFromUrl)
  const [isDark, setIsDark] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [focusRequest, setFocusRequest] = useState<MapFocusRequest | null>(null)

  const handleFocusMap = useCallback((latlng: LatLng) => {
    setFocusRequest((prev) => ({ latlng, seq: (prev?.seq ?? 0) + 1 }))
  }, [])

  useLocationUrlSync(locations)

  /** Reset all locations and navigate to root */
  const handleLogoClick = useCallback(() => {
    if (locations.length === 0) {
      // No locations to reset, just navigate
      window.history.replaceState(null, '', '/')
      return
    }
    if (window.confirm('出発地をリセットしていいですか？')) {
      setLocations([])
      window.history.replaceState(null, '', '/')
    }
  }, [locations.length])

  /* Sync theme attribute on <html> */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'cmyk')
  }, [isDark])

  const result: MeetingPointResult | null = useMemo(() => {
    if (locations.length < 2) return null
    const points = locations.map((l) => l.latlng)
    return {
      centroid: centroid(points),
      geometricMedian: geometricMedian(points),
      locations,
    }
  }, [locations])

  const centroidNearby = useNearbyStations(result?.centroid ?? null, NEARBY_STATION_LIMIT)
  const medianNearby = useNearbyStations(result?.geometricMedian ?? null, NEARBY_STATION_LIMIT)

  const suggestedStation = useMemo(() => {
    if (!result || medianNearby.stations.length === 0) return null
    const participants = result.locations.map((l) => l.latlng)
    return selectKMedoidStation(participants, medianNearby.stations)
  }, [result, medianNearby.stations])

  const isMaxReached = locations.length >= MAX_LOCATIONS

  function handleAddLocation(location: Location) {
    setLocations((prev) => (prev.length >= MAX_LOCATIONS ? prev : [...prev, location]))
  }

  function handleRemoveLocation(index: number) {
    setLocations((prev) => prev.filter((_, i) => i !== index))
  }

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildShareUrl(locations))
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch {
      // Clipboard API not available — fail silently
    }
  }, [locations])

  return (
    <div className="min-h-full lg:h-full bg-base-200 flex flex-col lg:overflow-hidden">
      <header className="navbar bg-base-100 shadow-sm">
        <div className="flex-1">
          <button
            type="button"
            onClick={handleLogoClick}
            className="btn btn-ghost h-auto min-h-0 p-0 px-4"
            aria-label="ホームに戻る"
          >
            <img src="/logo.svg" alt="rendez-vous" className="h-8" />
          </button>
        </div>
        <div className="flex-none px-4">
          <label className="swap swap-rotate" aria-label="テーマ切り替え">
            <input type="checkbox" checked={isDark} onChange={() => setIsDark((prev) => !prev)} />
            {/* Sun icon (shown in light mode) */}
            <svg
              className="swap-off h-6 w-6 fill-current"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
            >
              <title>Light mode</title>
              <path d="M5.64,17l-.71.71a1,1,0,0,0,0,1.41,1,1,0,0,0,1.41,0l.71-.71A1,1,0,0,0,5.64,17ZM5,12a1,1,0,0,0-1-1H3a1,1,0,0,0,0,2H4A1,1,0,0,0,5,12Zm7-7a1,1,0,0,0,1-1V3a1,1,0,0,0-2,0V4A1,1,0,0,0,12,5ZM5.64,7.05a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29,1,1,0,0,0,0-1.41l-.71-.71A1,1,0,0,0,4.93,6.34Zm12,.29a1,1,0,0,0,.7-.29l.71-.71a1,1,0,1,0-1.41-1.41L17,5.64a1,1,0,0,0,0,1.41A1,1,0,0,0,17.66,7.34ZM21,11H20a1,1,0,0,0,0,2h1a1,1,0,0,0,0-2Zm-9,8a1,1,0,0,0-1,1v1a1,1,0,0,0,2,0V20A1,1,0,0,0,12,19ZM18.36,17A1,1,0,0,0,17,18.36l.71.71a1,1,0,0,0,1.41,0,1,1,0,0,0,0-1.41ZM12,6.5A5.5,5.5,0,1,0,17.5,12,5.51,5.51,0,0,0,12,6.5Zm0,9A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z" />
            </svg>
            {/* Moon icon (shown in dark mode) */}
            <svg
              className="swap-on h-6 w-6 fill-current"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
            >
              <title>Dark mode</title>
              <path d="M21.64,13a1,1,0,0,0-1.05-.14,8.05,8.05,0,0,1-3.37.73A8.15,8.15,0,0,1,9.08,5.49a8.59,8.59,0,0,1,.25-2A1,1,0,0,0,8,2.36,10.14,10.14,0,1,0,22,14.05,1,1,0,0,0,21.64,13Zm-9.5,6.69A8.14,8.14,0,0,1,7.08,5.22v.27A10.15,10.15,0,0,0,17.22,15.63a9.79,9.79,0,0,0,2.1-.22A8.11,8.11,0,0,1,12.14,19.73Z" />
            </svg>
          </label>
        </div>
      </header>

      <main className="flex-1 flex flex-col lg:flex-row gap-4 p-4 min-h-0">
        {/* Left column: input form + result card (scrollable on desktop) + desktop-only footer pinned to bottom */}
        <aside className="w-full lg:w-md shrink-0 flex flex-col lg:overflow-hidden">
          <div className="flex flex-col gap-4 lg:flex-1 lg:overflow-y-auto overflow-x-hidden">
            <LocationForm onAdd={handleAddLocation} disabled={isMaxReached} />
            <ResultCard
              locations={locations}
              result={result}
              onRemove={handleRemoveLocation}
              centroidNearbyStations={centroidNearby.stations}
              medianNearbyStations={medianNearby.stations}
              suggestedStation={suggestedStation}
              isLoadingNearbyStations={centroidNearby.isLoading || medianNearby.isLoading}
              onCopyUrl={handleCopyUrl}
              isCopied={isCopied}
              onFocusMap={handleFocusMap}
            />
          </div>
          <Footer className="hidden lg:flex mt-4" />
        </aside>

        {/* Right column: map (fills remaining space) */}
        <div className="flex-1 min-w-0 min-h-64 lg:min-h-0">
          <MapView
            locations={locations}
            centroid={result?.centroid}
            geometricMedian={result?.geometricMedian}
            suggestedStation={suggestedStation}
            centroidNearbyStations={centroidNearby.stations}
            medianNearbyStations={medianNearby.stations}
            focusRequest={focusRequest}
          />
        </div>
      </main>

      {/* Mobile-only footer at site bottom (desktop footer is inside the left aside) */}
      <Footer className="flex lg:hidden" />
    </div>
  )
}

export default App
